import {
  Component,
  inject,
  Injector,
  INJECTOR,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { TuiCardLarge, TuiCardMedium } from '@taiga-ui/layout';
import { HeaderComponent } from '../header/header.component';
import { TuiAvatar, TuiAvatarStack } from '@taiga-ui/kit';
import { TuiDialogService, TuiSurface, tuiDialog } from '@taiga-ui/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../services/auth.service';
import { User } from '../models/user';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { PatientService } from '../services/patient.service';
import { OrdoclicAccountFormModalComponent } from '../ordoclic-account-form-modal/ordoclic-account-form-modal.component';
import { PrescriptionFormAerosolsComponent } from '../prescription-form/prescription-form-aerosols/prescription-form-aerosols.component';
import { PrescriptionFormAutreComponent } from '../prescription-form/prescription-form-autre/prescription-form-autre.component';
import { PrescriptionFormAvfComponent } from '../prescription-form/prescription-form-avf/prescription-form-avf.component';
import { PrescriptionFormPpcComponent } from '../prescription-form/prescription-form-ppc/prescription-form-ppc.component';
import { PrescriptionFormDialogData } from '../models/prescription-form-dialog-data';
import { UserService } from '../services/user.service';
import { DemandeContactModalComponent } from '../demande-contact/demande-contact-modal/demande-contact-modal.component';

@Component({
  selector: 'app-accueil-page',
  standalone: true,
  imports: [
    TuiCardLarge,
    HeaderComponent,
    TuiAvatarStack,
    TuiAvatar,
    TuiSurface,
    TuiCardMedium,
    RouterLink,
    CommonModule,
  ],
  templateUrl: './accueil-page.component.html',
  styleUrl: './accueil-page.component.scss',
})
export class AccueilPageComponent implements OnInit {
  user: User | null = null;

  private readonly patientService = inject(PatientService);
  protected readonly authService = inject(AuthService);
  private userService = inject(UserService);
  private router = inject(Router);

  nombrePrescriptionsEchues0: WritableSignal<number | undefined> =
    signal(undefined);

  nombrePrescriptionsEchues1mois0: WritableSignal<number | undefined> =
    signal(undefined);

  nombrePrescriptionsEchues2mois0: WritableSignal<number | undefined> =
    signal(undefined);

  nombrePrescriptionsInitiales0: WritableSignal<number | undefined> =
    signal(undefined);

  public userList: WritableSignal<User[] | undefined> = signal(undefined);

  private readonly injector = inject(INJECTOR);
  private readonly dialogService = inject(TuiDialogService);

  private readonly formPpc = tuiDialog(PrescriptionFormPpcComponent);
  private readonly formAvf = tuiDialog(PrescriptionFormAvfComponent);
  private readonly formAerosols = tuiDialog(PrescriptionFormAerosolsComponent);
  private readonly formAutre = tuiDialog(PrescriptionFormAutreComponent, {
    size: 'l',
  });

  ngOnInit(): void {
    var date: Date = new Date();
    var date1mois: Date = new Date();
    date1mois.setMonth(date1mois.getMonth() + 1);
    var date2mois: Date = new Date();
    date2mois.setMonth(date2mois.getMonth() + 2);

    this.patientService
      .pullList(undefined, undefined, undefined, undefined, date)
      .subscribe({
        next: (patients) => {
          this.nombrePrescriptionsEchues0.set(patients.total);
        },
      });

    this.patientService
      .pullList(undefined, undefined, undefined, date, date1mois)
      .subscribe({
        next: (patients) => {
          this.nombrePrescriptionsEchues1mois0.set(patients.total);
        },
      });

    this.patientService
      .pullList(undefined, undefined, undefined, date1mois, date2mois)
      .subscribe({
        next: (patients) => {
          this.nombrePrescriptionsEchues2mois0.set(patients.total);
        },
      });

    this.patientService
      .pullList(undefined, undefined, undefined, undefined, undefined, true)
      .subscribe({
        next: (patients) => {
          this.nombrePrescriptionsInitiales0.set(patients.total);
        },
      });

    this.user = this.authService.getUser();

    if (this.user?.role?.slug == 'liberal' && this.user?.ordocli_uuid == null) {
      this.openOrdoclicAccountFormModal();
    }

    this.userService.pullList(undefined, -1, undefined).subscribe({
      next: (data) => {
        this.userList.set(
          data.list.filter(
            (user) => user.rpps != null && user.rpps != undefined,
          ),
        );
      },
    });
  }

  openOrdoclicAccountFormModal() {
    this.dialogService
      .open(
        new PolymorpheusComponent(
          OrdoclicAccountFormModalComponent,
          this.injector,
        ),
      )
      .subscribe({
        complete: () => console.log('Dialog closed'),
      });
  }

  openForm(
    codeDActivite: 'PPC' | 'O2' | 'VENTILATION' | 'AVF' | 'AEROSOLS' | 'O2VNI',
  ): void {
    if (!this.userList()) {
      return;
    }

    switch (codeDActivite) {
      case 'PPC':
        var form = this.formPpc;
        break;
      case 'O2':
        var form = this.formAutre;
        break;
      case 'VENTILATION':
        var form = this.formAutre;
        break;
      case 'AVF':
        var form = this.formAvf;
        break;
      case 'AEROSOLS':
        var form = this.formAerosols;
        break;
      default:
        return;
        break;
    }

    form(
      new PrescriptionFormDialogData(
        undefined,
        undefined,
        codeDActivite,
        undefined,
        this.userList()!,
        null,
      ),
    ).subscribe();
  }

  // Méthode pour naviguer vers la liste des patients avec le bon filtre
  navigateToPatientList(filtre: number): void {
    let queryParams: any = {};
    if (filtre === 3) {
      queryParams = { initiale: 'true' };
    } else {
      queryParams = { echeance: filtre };
    }
    this.router.navigate(['/patient/list'], { queryParams });
  }

  openDemandeContactModal(): void {
    // Create a custom injector to pass the data to the modal
    const dialogInjector = Injector.create({
      providers: [
        {
          provide: 'patient', // You will inject this in the modal component
          useValue: undefined,
        },
      ],
      parent: this.injector,
    });

    this.dialogService
      .open(
        new PolymorpheusComponent(DemandeContactModalComponent, dialogInjector),
      )
      .subscribe();
  }
}
